import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Mail, ExternalLink, Send } from "lucide-react";
import { CAMP_API } from "@/pages/Campaigns";

const STATUS = { pending: ["Ready", "neutral"], queued: ["Queued", "warn"], sending: ["Sending", "accent"], sent: ["Sent", "accent"], handed_off: ["In Gingr", "accent"], failed: ["Failed", "neg"], skipped_booked: ["Booked, skipped", "neutral"] };
const parts = (t) => (t.length <= 320 ? "1 text" : "2 texts (1/2, 2/2)");

const EmailPreview = ({ camp, subject, body }) => {
  const [doc, setDoc] = useState("");
  useEffect(() => {
    const t = setTimeout(() => axios.post(`${CAMP_API}/${camp.id}/email-preview`, { subject, body }).then((r) => setDoc(r.data.html)).catch(() => toast.error("Couldn't load preview")), 300);
    return () => clearTimeout(t);
  }, [camp.id, subject, body]);
  return <iframe title="Email preview" srcDoc={doc} style={{ width: "100%", height: 620, border: "1px solid var(--line, #E2E8E8)", borderRadius: 8, background: "#ECECEA" }} data-testid="campaign-email-preview-frame" />;
};

const Preview = ({ camp, row, onSaved }) => {
  const email = camp.channel === "email";
  const [msg, setMsg] = useState(row.preview || "");
  const [subj, setSubj] = useState(row.preview_subject || "");
  const [tab, setTab] = useState("design");
  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${CAMP_API}/${camp.id}/test-email`, { subject: subj, body: msg });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send test email"); }
    setTesting(false);
  };
  useEffect(() => { setMsg(row.preview || ""); setSubj(row.preview_subject || ""); }, [row.owner_id, row.preview, row.preview_subject]);
  const locked = !["pending", "queued", "failed"].includes(row.status);
  const save = async () => {
    try {
      await axios.patch(`${CAMP_API}/${camp.id}/owners/${row.owner_id}`, email ? { message: msg, subject: subj } : { message: msg });
      toast.success("Message saved for this owner");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  const Ch = email ? Mail : MessageSquare;
  return (
    <div className="raised" style={{ borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10, alignSelf: "start", position: "sticky", top: 72 }} data-testid="campaign-preview">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span className="label" style={{ display: "flex", alignItems: "center", gap: 6 }}><Ch size={13} />{email ? "Email" : camp.channel === "sms" ? "Text" : "Owner"} to {row.first_name} {row.last_name}</span>
        <a href={row.gingr_url} target="_blank" rel="noreferrer" style={{ fontWeight: 700, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }} data-testid="campaign-preview-gingr-link">Open in Gingr <ExternalLink size={14} strokeWidth={2.5} /></a>
      </div>
      <div className="muted mono" style={{ fontSize: 11 }}>{email ? row.email : row.cell}</div>
      {camp.channel === "handoff" ? <div className="muted" style={{ fontSize: 13 }}>Design and send the message inside Gingr Marketing.</div> : (
        <>
          {email && (
            <div style={{ display: "flex", gap: 6 }}>
              {[["design", "Branded email"], ["edit", "Edit text"]].map(([k, l]) => (
                <button key={k} className={`btn btn-sm ${tab === k ? "btn-primary" : ""}`} onClick={() => setTab(k)} data-testid={`campaign-email-tab-${k}`}>{l}</button>
              ))}
            </div>
          )}
          {email && <input className="input" value={subj} disabled={locked} onChange={(e) => setSubj(e.target.value)} data-testid="campaign-preview-subject" />}
          {email && tab === "design" ? <EmailPreview camp={camp} subject={subj} body={msg} /> : (
            <textarea className="input" rows={email ? 11 : 6} value={msg} disabled={locked} onChange={(e) => setMsg(e.target.value)} data-testid="campaign-preview-message" />
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="mono muted" style={{ fontSize: 11 }} data-testid="campaign-preview-count">{email ? "Email · Outlook" : `${msg.length} chars · ${parts(msg)}`}</span>
            <div style={{ display: "flex", gap: 6 }}>
              {email && <button className="btn btn-sm" disabled={testing} onClick={sendTest} data-testid="campaign-send-test-email"><Send size={12} />{testing ? "Sending…" : "Send test to me"}</button>}
              {!locked && <button className="btn btn-sm btn-primary" onClick={save} data-testid="campaign-preview-save">Save for this owner</button>}
            </div>
          </div>
          {row.error && <div className="alert" style={{ fontSize: 12 }}>Last attempt failed: {row.error}</div>}
        </>
      )}
    </div>
  );
};

export const Review = ({ camp, reload, setCamp }) => {
  const [active, setActive] = useState(camp.audience[0]?.owner_id);
  const cur = camp.audience.find((a) => a.owner_id === active) || camp.audience[0];
  const toggle = async (ids, selected) => {
    setCamp({ ...camp, audience: camp.audience.map((a) => (!ids || ids.includes(a.owner_id) ? { ...a, selected } : a)) });
    await axios.post(`${CAMP_API}/${camp.id}/select`, { owner_ids: ids, selected }).then(reload).catch(() => toast.error("Couldn't update selection"));
  };
  if (!camp.audience.length) return <div className="muted" style={{ fontSize: 13 }} data-testid="campaign-audience-empty">No owners match these rules right now.</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(300px, 1fr)", gap: 16 }} className="camp-grid">
      <div style={{ overflowX: "auto", maxHeight: 640, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <button className="btn btn-sm" onClick={() => toggle(null, true)} data-testid="campaign-select-all">Select all</button>
          <button className="btn btn-sm" style={{ background: "#111", color: "#fff", borderColor: "#111", fontWeight: 700 }} onClick={() => toggle(null, false)} data-testid="campaign-select-none">Unselect all</button>
        </div>
        <table className="table" style={{ width: "100%" }}>
          <thead><tr><th /><th style={{ textAlign: "left" }}>#</th><th style={{ textAlign: "left" }}>Owner</th><th style={{ textAlign: "left" }}>Group</th><th style={{ textAlign: "left" }}>History</th><th style={{ textAlign: "left" }}>Status</th></tr></thead>
          <tbody>
            {camp.audience.map((a, i) => (
              <tr key={a.owner_id} onClick={() => setActive(a.owner_id)} style={{ cursor: "pointer", background: a.owner_id === cur?.owner_id ? "rgba(31,138,124,.07)" : undefined }} data-testid={`aud-row-${a.owner_id}`}>
                <td onClick={(e) => e.stopPropagation()}><input type="checkbox" className="cb" checked={!!a.selected} disabled={!["pending", "queued", "failed"].includes(a.status)} onChange={(e) => toggle([a.owner_id], e.target.checked)} data-testid={`aud-check-${a.owner_id}`} /></td>
                <td className="mono muted">{i + 1}</td>
                <td style={{ whiteSpace: "nowrap" }}><b>{a.first_name} {a.last_name}</b><div className="muted" style={{ fontSize: 11 }}>{a.pets}</div></td>
                <td><span className={`badge ${a.group === "multi_boarder" ? "accent" : "neutral"}`} style={{ whiteSpace: "nowrap" }}>{camp.groups[a.group][0]}</span></td>
                <td className="muted mono" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{a.boarding_visits} boarding · {a.daycare_visits} daycare<div>Last {a.last_visit || "—"}</div></td>
                <td><span className={`badge ${STATUS[a.status]?.[1] || "neutral"}`}>{STATUS[a.status]?.[0] || a.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cur && <Preview camp={camp} row={cur} onSaved={reload} />}
      <style>{`@media (max-width: 900px){ .dc .camp-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
};
